import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Mail, Phone, MapPin, Truck, Shield, CreditCard, Smartphone } from 'lucide-react';
import { API_URL } from '../api';
import { useNewsletterSubscribe } from '../hooks/useNewsletterSubscribe';

interface FooterLink {
  _id: string;
  title: string;
  url: string;
}

interface FooterSection {
  _id: string;
  sectionName: string;
  links: FooterLink[];
}

interface StoreSettings {
  storeName: string;
  storeEmail: string;
  supportEmail: string;
  supportPhone: string;
  storeAddress: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  linkedin?: string;
}

/** Footer links the storefront should not show (removed from web). */
const REMOVED_FOOTER_TITLES = new Set(
  [
    'careers',
    'press',
    'blog',
    'sustainability',
    'investors',
    'returns & exchanges',
    'returns and exchanges',
    'shipping information',
    'size guide',
    'faq',
    'terms of service',
    'cookie policy',
    'accessibility',
    'terms of sale',
  ].map((s) => s.toLowerCase())
);

const REMOVED_FOOTER_PATHS = new Set([
  '/careers',
  '/press',
  '/blog',
  '/sustainability',
  '/investors',
  '/returns-exchanges',
  '/returns',
  '/returns-and-exchanges',
  '/shipping',
  '/shipping-information',
  '/size-guide',
  '/faq',
  '/terms-of-service',
  '/cookie-policy',
  '/accessibility',
  '/terms-of-sale',
]);

function normalizeFooterPath(url: string): string {
  if (!url || typeof url !== 'string') return '';
  const s = url.trim();
  if (!s) return '';
  try {
    if (s.startsWith('http://') || s.startsWith('https://')) {
      const u = new URL(s);
      let p = u.pathname.toLowerCase();
      if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
      return p || '/';
    }
  } catch {
    /* ignore */
  }
  let p = s.toLowerCase();
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function shouldRemoveFooterLink(title: string, url: string): boolean {
  const t = (title || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (REMOVED_FOOTER_TITLES.has(t)) return true;
  if (/returns/.test(t) && /exchange/.test(t)) return true;
  const p = normalizeFooterPath(url);
  if (REMOVED_FOOTER_PATHS.has(p)) return true;
  if (p === '/blog' || p.startsWith('/blog/')) return true;
  return false;
}

function filterFooterSections(sections: FooterSection[]): FooterSection[] {
  return sections
    .map((section) => ({
      ...section,
      links: (section.links || []).filter((link) => !shouldRemoveFooterLink(link.title, link.url)),
    }))
    .filter((section) => section.links.length > 0);
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [footerSections, setFooterSections] = useState<FooterSection[]>([]);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    storeName: 'NLS Store',
    storeEmail: 'support@nlsstore.com',
    supportEmail: 'support@nlsstore.com',
    supportPhone: '+1 (555) 123-4567',
    storeAddress: '123 Fashion Ave, NY 10001'
  });
  const newsletter = useNewsletterSubscribe('footer');

  // Fetch store settings from API with polling for real-time updates
  useEffect(() => {
    const fetchStoreSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/ultra-settings/public/store`);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        
        if (data.success && data.data) {
          // Priority: new footerContact fields > old support fields > defaults
          const newSettings = {
            storeName: data.data.storeName || 'NLS Store',
            storeEmail: data.data.storeEmail || 'support@nlsstore.com',
            supportEmail: data.data.footerContactEmail || data.data.supportEmail || data.data.storeEmail || 'support@nlsstore.com',
            supportPhone: data.data.footerContactPhone || data.data.supportPhone || '+1 (555) 123-4567',
            storeAddress: data.data.footerContactAddress || data.data.storeAddress || '123 Fashion Ave, NY 10001',
            facebook: data.data.facebook || '',
            instagram: data.data.instagram || '',
            twitter: data.data.twitter || '',
            youtube: data.data.youtube || '',
            linkedin: data.data.linkedin || ''
          };
          setStoreSettings(newSettings);
        }
      } catch (error) {
        // Silently fail - use default values already set in state
      }
    };
    
    // Fetch immediately on mount
    fetchStoreSettings();
    
    // Refresh store copy periodically (avoid tight polling / layout thrash)
    const intervalId = setInterval(fetchStoreSettings, 60000);
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Fetch footer sections from API
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/navigation-menu/footer`);
        const data = await res.json();
        if (data.success && data.data) {
          const raw = data.data.map((section: any) => ({
            _id: section._id,
            sectionName: section.sectionName || section.title,
            links: section.links || [],
          })) as FooterSection[];
          setFooterSections(filterFooterSections(raw));
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
        setFooterSections([]);
      }
    };
    fetchFooterData();
  }, []);

  return (
    <footer className="bg-gray-900 text-white mt-auto border-t border-gray-800">
      {/* Newsletter — compact band */}
      <div className="bg-primary-600 py-5 md:py-6">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 md:gap-6">
            <div className="text-center md:text-left min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1">Subscribe to our newsletter</h3>
              <p className="text-primary-100/95 text-sm sm:text-base leading-snug max-w-xl mx-auto md:mx-0">
                Offers &amp; new arrivals — no spam.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto md:max-w-md shrink-0">
            <form
              className="flex flex-col sm:flex-row gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                newsletter.subscribe();
              }}
            >
              <input
                type="email"
                placeholder="Your email"
                value={newsletter.email}
                onChange={(e) => newsletter.setEmail(e.target.value)}
                disabled={newsletter.loading || newsletter.done}
                required
                className="px-3 py-2.5 rounded-lg text-gray-900 text-sm w-full sm:min-w-[200px] focus:outline-none focus:ring-2 focus:ring-white/80 disabled:opacity-70"
              />
              <button
                type="submit"
                disabled={newsletter.loading || newsletter.done}
                className="px-4 py-2.5 rounded-lg bg-white text-primary-700 text-sm font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap disabled:opacity-70"
              >
                {newsletter.loading ? '…' : newsletter.done ? 'Subscribed' : 'Subscribe'}
              </button>
            </form>
            {newsletter.message && (
              <p
                className={`text-sm w-full text-center md:text-right ${
                  newsletter.message.type === 'success' ? 'text-green-200' : 'text-red-200'
                }`}
              >
                {newsletter.message.text}
              </p>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip — smaller icons vs text */}
      <div className="bg-gray-800/95 py-4 md:py-5 border-b border-gray-700/80">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="flex items-start gap-2.5 min-w-0">
              <Truck className="w-5 h-5 md:w-6 md:h-6 text-primary-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-white leading-tight">Fast delivery</h4>
              </div>
            </div>
            <div className="flex items-start gap-2.5 min-w-0">
              <Shield className="w-5 h-5 md:w-6 md:h-6 text-primary-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-white leading-tight">Genuine products</h4>
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">Quality checked</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 min-w-0">
              <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-primary-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-white leading-tight">Secure payment</h4>
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">Encrypted checkout</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 min-w-0">
              <Phone className="w-5 h-5 md:w-6 md:h-6 text-primary-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-white leading-tight">Help</h4>
                <p className="text-xs text-gray-400 mt-0.5 break-all">{storeSettings.supportPhone}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main columns */}
      <div className="py-8 md:py-10">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Dynamic Footer Sections from Admin - Filter out Categories */}
            {footerSections.length > 0 ? (
              footerSections
                .filter((section) => section.sectionName?.toLowerCase() !== 'categories')
                .map((section) => (
                <div key={section._id} className="sm:col-span-1 lg:col-span-2 min-w-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-3">
                    {section.sectionName}
                  </h3>
                  <ul className="space-y-1.5">
                    {section.links.map((link) => (
                      <li key={link._id}>
                        <Link
                          to={link.url}
                          className="text-sm text-gray-400 hover:text-white transition-colors break-words"
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <>
                <div className="sm:col-span-1 lg:col-span-2 min-w-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-3">Customer service</h3>
                  <ul className="space-y-1.5">
                    <li>
                      <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                        Contact us
                      </Link>
                    </li>
                    <li>
                      <Link to="/track-order" className="text-sm text-gray-400 hover:text-white transition-colors">
                        Track order
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="sm:col-span-1 lg:col-span-2 min-w-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-3">About</h3>
                  <ul className="space-y-1.5">
                    <li>
                      <Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">
                        About us
                      </Link>
                    </li>
                    <li>
                      <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                        Contact
                      </Link>
                    </li>
                  </ul>
                </div>
              </>
            )}

            {/* Contact — readable, wrapped; no oversized visual weight */}
            <div className="sm:col-span-2 lg:col-span-4 min-w-0 rounded-xl border border-gray-700/80 bg-gray-800/40 p-4 md:p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-3">Contact</h3>
              <div className="space-y-2.5 text-sm text-gray-300">
                <div className="flex gap-2.5 min-w-0">
                  <Phone className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" aria-hidden />
                  <a href={`tel:${String(storeSettings.supportPhone).replace(/\s/g, '')}`} className="break-all hover:text-white transition-colors">
                    {storeSettings.supportPhone}
                  </a>
                </div>
                <div className="flex gap-2.5 min-w-0">
                  <Mail className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" aria-hidden />
                  <a href={`mailto:${storeSettings.supportEmail}`} className="break-all hover:text-white transition-colors">
                    {storeSettings.supportEmail}
                  </a>
                </div>
                <div className="flex gap-2.5 min-w-0">
                  <MapPin className="w-4 h-4 text-primary-400 shrink-0 mt-1" aria-hidden />
                  <span className="leading-relaxed text-gray-400 break-words">{storeSettings.storeAddress}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700/80">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Follow</h4>
                <div className="flex flex-wrap gap-2">
                  {storeSettings.facebook && (
                    <a
                      href={storeSettings.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                  {storeSettings.instagram && (
                    <a
                      href={storeSettings.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {storeSettings.twitter && (
                    <a
                      href={storeSettings.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 transition-colors"
                      aria-label="Twitter"
                    >
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {storeSettings.youtube && (
                    <a
                      href={storeSettings.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 transition-colors"
                      aria-label="YouTube"
                    >
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                  {storeSettings.linkedin && (
                    <a
                      href={storeSettings.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-800/90 hover:bg-gray-700 transition-colors"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment row — compact */}
      <div className="border-t border-gray-800 py-4 md:py-5">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-center md:text-left">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Payments</h4>
              <p className="text-xs text-gray-500 mt-0.5">Major cards &amp; UPI where enabled</p>
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-4 gap-y-2 text-xs text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="w-4 h-4 shrink-0" aria-hidden />
                Cards
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Smartphone className="w-4 h-4 shrink-0" aria-hidden />
                UPI / wallets
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-800 py-3">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
            <p className="text-xs text-gray-500">
              © {currentYear} {storeSettings.storeName}. All rights reserved.
            </p>
            <div className="flex gap-4 text-xs">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-300 transition-colors">
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
